/**
 * ISO certification cycle rule set — V106-5-5 / ADR-0080 §5 (migration 531b).
 *
 * The cycle a certification body runs (stage 1, stage 2, two surveillance
 * audits, recertification before expiry) is reviewed, versioned content, not
 * code (D-102-17): family `iso-certification`, schema
 * `gacpro.program-rules.iso-certification/1`. Values come from ISO/IEC
 * 17021-1:2015 (normative) or are marked planning defaults where the standard
 * fixes no number. A new version (e.g. after ISO/IEC 42006 is read) moves every
 * not-done due date with no deploy: the schedule is computed at read time from
 * the current approved version and the milestones people record.
 *
 * This module is pure (no DB): the schema and the schedule calculator.
 */
import { z } from 'zod';
export const ISO_CERTIFICATION_FAMILY = 'iso-certification';
export const ISO_CERTIFICATION_SCHEMA = 'gacpro.program-rules.iso-certification/1';
const text = z.string().trim().min(1).max(500);
const KEY = /^[a-z][a-z0-9_]{1,40}$/;
/** Anchors every rule set knows; an event may also anchor on an earlier event's key. */
export const BUILT_IN_ANCHORS = ['cycle_start', 'certification_decision'];
const Offset = z.object({
    months: z.number().int().min(-120).max(120),
    days: z.number().int().min(-366).max(366),
}).strict();
const CycleEvent = z.object({
    key: z.string().regex(KEY),
    label: text,
    kind: z.enum(['stage1', 'stage2', 'surveillance', 'recertification']),
    anchor: z.string().regex(KEY),
    /** When the event is planned, relative to its anchor. */
    due: Offset,
    /** The latest date the standard allows, relative to the same anchor (normative only). */
    deadline: Offset.optional(),
    /** Only in the first (initial-certification) cycle. */
    initial_only: z.boolean(),
    /** `normative`: the timing (the deadline when there is one) is in the cited clause; `planning_default`: the standard fixes none. */
    basis: z.enum(['normative', 'planning_default']),
    source: text,
}).strict();
export const IsoCertificationRulesV1 = z.object({
    schema: z.literal(ISO_CERTIFICATION_SCHEMA),
    program_key: z.literal('iso-42001'),
    cycle_months: z.number().int().min(12).max(72),
    events: z.array(CycleEvent).min(1).max(12),
    /** A planned event within this many days of its due date reads `due`. */
    due_soon_days: z.number().int().min(0).max(180),
    /** ISO/IEC 17021-1 §9.5.3.2: corrections for a major nonconformity verified within N months of stage 2's last day (read by Track 6). */
    major_nonconformity_verify_months: z.number().int().min(1).max(24),
    /** ISO/IEC 17021-1 §9.6.3.2.5: certification can be restored within N months after expiry. */
    restoration_months: z.number().int().min(0).max(24),
    sources: z.array(z.object({ title: text, url: z.string().url().max(500), version: text }).strict()).min(1).max(10),
}).strict().superRefine((r, ctx) => {
    const issue = (message) => ctx.addIssue({ code: 'custom', message });
    const seen = new Set();
    for (const e of r.events) {
        if (seen.has(e.key))
            issue(`duplicate event ${e.key}`);
        if (BUILT_IN_ANCHORS.includes(e.key))
            issue(`event key ${e.key} is a built-in anchor`);
        if (!BUILT_IN_ANCHORS.includes(e.anchor) && !seen.has(e.anchor))
            issue(`event ${e.key} anchors on ${e.anchor}, which is neither a built-in anchor nor an earlier event`);
        if (e.anchor !== 'cycle_start' && e.anchor !== 'certification_decision') {
            const a = r.events.find((x) => x.key === e.anchor);
            if (a && a.initial_only && !e.initial_only)
                issue(`event ${e.key} runs every cycle but anchors on initial-only ${e.anchor}`);
        }
        if (e.deadline && e.basis !== 'normative')
            issue(`event ${e.key}: a deadline is a normative limit, so basis must be normative`);
        seen.add(e.key);
    }
    if (!r.events.some((e) => e.kind === 'recertification'))
        issue('a cycle needs a recertification event');
});
const DAY_MS = 86_400_000;
const isoDay = (d) => d.toISOString().slice(0, 10);
/** `YYYY-MM-DD` + months + days (UTC; month-end clamps, e.g. Jan 31 + 1 month = Feb 28/29). */
export function addOffset(date, off) {
    const [y, m, d] = date.slice(0, 10).split('-').map(Number);
    const total = y * 12 + (m - 1) + off.months;
    const ny = Math.floor(total / 12);
    const nm = total - ny * 12;
    const last = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate();
    const base = Date.UTC(ny, nm, Math.min(d, last));
    return isoDay(new Date(base + off.days * DAY_MS));
}
/**
 * The cycle's events with due dates, from the rules and the milestones of
 * `cycleNo`. A recorded milestone with an event's key marks it done. An anchor
 * not recorded yet is estimated from its own due date (`anchor_estimated`).
 * `plannedCycleStart` (the profile's planned date, V108-8-15), else
 * `fallbackCycleStart` (the profile's first period start), stands in for an
 * unrecorded `cycle_start` in cycle 1.
 */
export function isoCertificationSchedule(rules, input) {
    const ms = new Map(input.milestones.filter((m) => m.cycle_no === input.cycleNo).map((m) => [m.milestone_key, m.occurred_on.slice(0, 10)]));
    const initial = input.cycleNo === 1;
    const events = rules.events.filter((e) => initial || !e.initial_only);
    const recordedStart = ms.get('cycle_start') ?? null;
    const planned = initial && !recordedStart ? (input.plannedCycleStart ?? null) : null;
    const cycleStart = recordedStart ?? planned ?? (initial ? input.fallbackCycleStart : null);
    const dates = new Map();
    dates.set('cycle_start', { on: cycleStart, estimated: !recordedStart });
    // certification_decision: recorded, else (cycle 1) the last initial-only event's date (stage 2).
    const decisionRecorded = ms.get('certification_decision') ?? null;
    const out = [];
    const resolve = (key) => {
        if (key === 'certification_decision') {
            if (decisionRecorded)
                return { on: decisionRecorded, estimated: false };
            const lastInitial = [...out].reverse().find((e) => rules.events.find((r) => r.key === e.key)?.initial_only);
            return { on: lastInitial ? (lastInitial.done_on ?? lastInitial.due_on) : null, estimated: true };
        }
        return dates.get(key) ?? { on: null, estimated: true };
    };
    for (const e of events) {
        const a = resolve(e.anchor);
        const due = a.on ? addOffset(a.on, e.due) : null;
        const deadline = a.on && e.deadline ? addOffset(a.on, e.deadline) : null;
        const done = ms.get(e.key) ?? null;
        let status = 'planned';
        if (done)
            status = 'done';
        else if ((deadline && deadline < input.today) || (!deadline && due && due < input.today))
            status = 'overdue';
        else if (due && addOffset(input.today, { months: 0, days: rules.due_soon_days }) >= due)
            status = 'due';
        out.push({
            key: e.key, label: e.label, kind: e.kind, anchor: e.anchor, anchor_on: a.on, anchor_estimated: a.estimated,
            due_on: due, deadline_on: deadline, done_on: done, status, basis: e.basis, source: e.source,
        });
        dates.set(e.key, { on: done ?? due, estimated: !done || a.estimated });
    }
    const decision = resolve('certification_decision');
    return {
        cycle_no: input.cycleNo,
        cycle_start_on: cycleStart,
        cycle_start_basis: recordedStart ? 'recorded' : planned ? 'planned' : cycleStart ? 'first_period' : 'none',
        certification_decision_on: decision.on,
        certification_decision_estimated: decision.estimated,
        expires_on: decision.on ? addOffset(decision.on, { months: rules.cycle_months, days: 0 }) : null,
        events: out,
    };
}
//# sourceMappingURL=iso-certification-rules.js.map